import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Search as SearchIcon, Compass, Calculator, Scale, FileCheck,
  Car, Truck, Gem, ChevronRight, MapPin,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { HERO_IMAGE } from "../lib/vehicleImages";

const CLASSICS_IMG = "https://images.unsplash.com/photo-1784379926533-c84b04e6e808?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const COUNTRIES = [
  ["DE", "Germany"], ["NL", "Netherlands"], ["ES", "Spain"], ["BE", "Belgium"],
  ["FR", "France"], ["IT", "Italy"], ["PL", "Poland"], ["AT", "Austria"], ["SE", "Sweden"], ["CZ", "Czech Republic"],
];
const CORRIDORS = [
  ["Germany", "Netherlands"], ["Germany", "Spain"], ["Belgium", "Netherlands"],
  ["France", "Belgium"], ["Italy", "Germany"],
];
const CATEGORIES = [
  ["Cars", Car], ["SUVs & 4x4", Truck], ["Sedans", Car], ["Station wagons", Car],
  ["Hatchbacks", Car], ["Coupés", Car], ["Cabrio", Car], ["MPV", Car],
  ["Commercial", Truck], ["Classics", Gem],
];
const FEATURES = [
  [Compass, "Sourcing Intelligence", "Find attractive vehicle opportunities across European markets — not just listings, but where the economics actually work."],
  [Calculator, "Total Cost Calculation", "Estimate the real acquisition cost: purchase, transport, taxes, registration, inspection and repairs — in one currency."],
  [Scale, "Margin & Risk Analysis", "Understand expected margin, a separate risk score and a confidence level before you commit."],
  [FileCheck, "Opportunity Report", "Receive a clear BUY / NEGOTIATE / PASS recommendation with the maximum price you should pay."],
];
const BRANDS = ["Volkswagen", "BMW", "Mercedes-Benz", "Audi", "Toyota", "Ford", "Volvo", "Porsche", "Renault", "Kia"];
const FLOW = ["Source market", "Vehicle", "Acquisition costs", "Destination market", "Market value", "Expected margin", "Atlas decision"];

const Overline = ({ children, className = "" }) => (
  <div className={`text-[11px] uppercase tracking-[0.18em] font-mono text-atlas-blue ${className}`}>{children}</div>
);

// Known multi-word manufacturers so "Mercedes-Benz C-Class" splits correctly.
const MULTIWORD_MAKES = [
  "Mercedes-Benz", "Alfa Romeo", "Land Rover", "Aston Martin", "Range Rover",
  "Rolls-Royce", "Great Wall",
];
const splitVehicle = (raw) => {
  const text = (raw || "").trim().replace(/\s+/g, " ");
  if (!text) return { make: "", model: "" };
  const hit = MULTIWORD_MAKES.find((m) => text.toLowerCase().startsWith(m.toLowerCase()));
  if (hit) return { make: hit, model: text.slice(hit.length).trim() };
  const [first, ...rest] = text.split(" ");
  return { make: first, model: rest.join(" ") };
};
const buildWorkspaceQuery = ({ q, src, dst, maxP, cat }) => {
  const { make, model } = splitVehicle(q);
  const p = new URLSearchParams();
  if (make) p.set("make", make);
  if (model) p.set("model", model);
  if (src) p.set("source", src);
  if (dst) p.set("dest", dst);
  if (maxP) p.set("asking", maxP);
  if (cat === "Classics") p.set("classification", "Classics");
  const qs = p.toString();
  return `/intelligence/new${qs ? `?${qs}` : ""}`;
};

const OpportunitySearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("DE");
  const [dst, setDst] = useState("NL");
  const [maxP, setMaxP] = useState("");
  const [cat, setCat] = useState("");
  const target = buildWorkspaceQuery({ q, src, dst, maxP, cat });
  const go = () => navigate(target);
  const cls = "h-12 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-atlas-blue/30";
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-[0_24px_70px_-20px_rgba(6,18,36,0.45)]" data-testid="opp-search-panel">
      <div className="px-5 md:px-7 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-slate-900">Find an acquisition opportunity</h2>
        <span className="text-[11px] font-mono text-slate-400 hidden sm:inline">Source → Destination · Europe</span>
      </div>
      <div className="p-5 md:p-7">
        <div className="grid md:grid-cols-12 gap-3">
          <div className="md:col-span-4 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Make & model (e.g. BMW 3 Series)"
              className={`${cls} w-full pl-9`} data-testid="opp-search-vehicle" />
          </div>
          <select value={src} onChange={(e) => setSrc(e.target.value)} className={`${cls} md:col-span-2`} data-testid="opp-search-source">
            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
          <select value={dst} onChange={(e) => setDst(e.target.value)} className={`${cls} md:col-span-2`} data-testid="opp-search-dest">
            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
          <input value={maxP} onChange={(e) => setMaxP(e.target.value)} type="number" placeholder="Max price €"
            className={`${cls} md:col-span-2`} data-testid="opp-search-maxprice" />
          <select value={cat} onChange={(e) => setCat(e.target.value)} className={`${cls} md:col-span-2`} data-testid="opp-search-category">
            <option value="">Any category</option>
            {CATEGORIES.map(([c]) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4">
          <Link to={target} className="text-sm font-medium text-atlas-ink hover:text-atlas-blue inline-flex items-center gap-1.5" data-testid="opp-search-advanced">
            Advanced analysis <ArrowRight className="h-4 w-4" />
          </Link>
          <Button onClick={go} className="h-12 px-7 bg-atlas-blue hover:bg-atlas-ink text-white text-base" data-testid="opp-search-submit">
            Search opportunities
          </Button>
        </div>
      </div>
    </div>
  );
};

const DEMO = {
  title: "BMW 320d", corridor: "Germany → Netherlands",
  rows: [["Purchase price", "€18,500"], ["Estimated landed cost", "€21,100"], ["Estimated market value", "€25,000"], ["Expected gross margin", "€3,900", true]],
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-atlas-navy overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(6,18,36,0.86) 0%, rgba(10,26,47,0.92) 60%, #0A1A2F 100%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-28 md:pb-36">
          <div className="max-w-3xl">
            <Overline className="text-atlas-sky mb-4">Atlas · European Vehicle Acquisition Intelligence</Overline>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.02]">
              Know what to buy, where,<br className="hidden sm:block" /> and <span className="text-atlas-sky">what to pay.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed">
              Find better vehicles. See real market value. Calculate total costs. Make smarter buying decisions across Europe.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/intelligence" data-testid="hero-find-opportunities">
                <Button className="h-12 px-7 bg-atlas-blue hover:bg-atlas-sky text-white text-base">Find opportunities</Button>
              </Link>
              <a href="#how" data-testid="hero-how-it-works">
                <Button variant="outline" className="h-12 px-7 border-white/25 text-white hover:bg-white/10 bg-transparent text-base">How Atlas works</Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 mt-9" data-testid="hero-corridors">
              <span className="text-xs text-slate-400 font-mono mr-1">Cross-border corridors:</span>
              {CORRIDORS.map(([a, b]) => (
                <span key={`${a}-${b}`} className="inline-flex items-center gap-1.5 text-xs text-slate-200 border border-white/15 bg-white/5 rounded-full px-3 py-1.5">
                  {a} <ChevronRight className="h-3 w-3 text-atlas-sky" /> {b}
                </span>
              ))}
              <span className="text-xs text-slate-500">and more across Europe</span>
            </div>
          </div>
        </div>
      </section>

      {/* OPPORTUNITY SEARCH PANEL (overlap) */}
      <section className="relative -mt-16 md:-mt-20 z-10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8"><OpportunitySearch /></div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 pt-14 pb-6">
        <div className="flex items-end justify-between mb-5">
          <Overline>Browse by category</Overline>
          <Link to="/atlas" className="text-sm text-atlas-blue hover:underline">All vehicles</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="home-categories">
          {CATEGORIES.map(([label, Icon], i) => (
            <Link key={label} to="/atlas" data-testid={`home-category-${i}`}
              className={`flex items-center gap-2.5 rounded-lg border px-4 py-3.5 transition-colors ${label === "Classics" ? "border-amber-200 bg-amber-50 hover:border-amber-400" : "border-slate-200 bg-white hover:border-atlas-blue"}`}>
              <Icon className={`h-5 w-5 ${label === "Classics" ? "text-amber-600" : "text-atlas-blue"}`} strokeWidth={1.6} />
              <span className="text-sm font-medium text-slate-800">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="how" className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
        <Overline className="mb-3">What Atlas does</Overline>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-atlas-ink mb-10 max-w-2xl">From a vehicle to a decision — with the economics in between.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-features">
          {FEATURES.map(([Icon, title, text], i) => (
            <div key={title} data-testid={`home-feature-${i}`} className="rounded-lg border border-slate-200 bg-white p-6 hover:border-atlas-blue transition-colors">
              <div className="h-10 w-10 rounded-md bg-atlas-blue/10 flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-atlas-blue" strokeWidth={1.7} />
              </div>
              <h3 className="font-display text-lg font-semibold text-atlas-ink">{title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OPPORTUNITY PREVIEW */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Overline className="mb-3">This is not a car listing</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-atlas-ink leading-tight">Atlas analyses the economics of the acquisition.</h2>
            <p className="text-slate-600 mt-5 leading-relaxed max-w-lg">
              Every opportunity is scored on real numbers — landed cost, destination market value, expected margin, risk and confidence — ending in a clear recommendation and the maximum price you should pay.
            </p>
            <Link to="/intelligence" className="inline-flex items-center gap-2 mt-6 text-atlas-blue font-medium hover:gap-3 transition-all">
              See how opportunities are scored <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_20px_60px_-25px_rgba(6,18,36,0.4)] overflow-hidden" data-testid="home-opp-preview">
            <div className="bg-atlas-ink text-white px-6 py-4 flex items-center justify-between">
              <div>
                <div className="font-display text-xl font-bold">{DEMO.title}</div>
                <div className="text-xs text-slate-300 flex items-center gap-1.5 mt-0.5"><MapPin className="h-3.5 w-3.5 text-atlas-sky" /> {DEMO.corridor}</div>
              </div>
              <span className="text-xs font-bold px-3 py-1.5 rounded-md bg-emerald-500 text-white">BUY</span>
            </div>
            <div className="p-6">
              <div className="space-y-2.5">
                {DEMO.rows.map(([label, val, strong]) => (
                  <div key={label} className={`flex items-center justify-between text-sm ${strong ? "border-t border-slate-200 pt-2.5" : ""}`}>
                    <span className={strong ? "font-semibold text-atlas-ink" : "text-slate-600"}>{label}</span>
                    <span className={`font-mono ${strong ? "font-bold text-atlas-ink" : "text-slate-800"}`}>{val}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-5">
                {[["Risk", "Low", "text-emerald-700 bg-emerald-50 border-emerald-200"], ["Confidence", "High", "text-emerald-700 bg-emerald-50 border-emerald-200"], ["Score", "87/100", "text-atlas-blue bg-atlas-blue/5 border-atlas-blue/20"]].map(([k, v, c]) => (
                  <div key={k} className={`rounded-md border px-3 py-2.5 text-center ${c}`}>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div>
                    <div className="font-mono font-semibold text-sm mt-0.5">{v}</div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-4 font-mono">Illustrative example · not a live listing</p>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR BRANDS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
        <Overline className="mb-5">Popular brands across Europe</Overline>
        <div className="flex flex-wrap gap-3" data-testid="home-brands">
          {BRANDS.map((b, i) => (
            <Link key={b} to={`/atlas?make=${encodeURIComponent(b)}`} data-testid={`home-brand-${i}`}
              className="px-5 py-3 rounded-lg border border-slate-200 bg-white hover:border-atlas-blue transition-colors">
              <span className="font-display text-base font-semibold text-atlas-ink tracking-tight">{b}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* EUROPEAN INTELLIGENCE FLOW */}
      <section className="bg-atlas-night text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
          <Overline className="text-atlas-sky mb-3">Cross-border intelligence</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">One European market. Thousands of opportunities.</h2>
          <p className="text-slate-400 mt-4 max-w-2xl">Atlas follows the value of a vehicle from the source market to the destination — and turns it into a decision.</p>
          <div className="mt-10 flex flex-wrap items-stretch gap-2" data-testid="home-flow">
            {FLOW.map((step, i) => (
              <React.Fragment key={step}>
                <div className={`flex-1 min-w-[130px] rounded-lg border px-4 py-4 ${i === FLOW.length - 1 ? "border-atlas-sky/40 bg-atlas-blue/15" : "border-white/10 bg-white/5"}`}>
                  <div className="font-mono text-[10px] text-slate-500">{String(i + 1).padStart(2, "0")}</div>
                  <div className={`text-sm font-medium mt-1 ${i === FLOW.length - 1 ? "text-atlas-sky" : "text-slate-200"}`}>{step}</div>
                </div>
                {i < FLOW.length - 1 && <div className="hidden lg:flex items-center text-slate-600"><ChevronRight className="h-4 w-4" /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ATLAS CLASSICS TEASER */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
        <div className="rounded-2xl overflow-hidden border border-slate-200 grid lg:grid-cols-2">
          <div className="relative min-h-[280px] bg-atlas-ink">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${CLASSICS_IMG})` }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(9,18,31,0.35), rgba(9,18,31,0.65))" }} />
          </div>
          <div className="bg-atlas-ink text-white p-8 md:p-10">
            <Overline className="text-amber-400 mb-3">A premium Atlas vertical</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Atlas Classics</h2>
            <p className="text-slate-300 mt-4 leading-relaxed max-w-md">Specialised intelligence for classic and collector vehicles — where value depends on more than mileage and year.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {["Originality", "Provenance", "Condition", "Restoration", "Specialist market value", "European sourcing"].map((t) => (
                <span key={t} className="text-xs text-slate-200 border border-white/15 bg-white/5 rounded-full px-3 py-1.5">{t}</span>
              ))}
            </div>
            <Link to="/intelligence" data-testid="home-classics-cta" className="inline-flex items-center gap-2 mt-7 text-amber-400 font-medium hover:gap-3 transition-all">
              Explore Atlas Classics <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* DEALER B2B */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Overline className="mb-3">Built for professionals</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-atlas-ink leading-tight">Built for people who buy cars professionally.</h2>
            <Link to="/list-your-source" data-testid="home-dealer-cta">
              <Button className="mt-6 h-12 px-7 bg-atlas-ink hover:bg-atlas-blue text-white text-base">For Dealers</Button>
            </Link>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {["Find better opportunities", "Compare European markets", "Know what to pay", "Estimate real acquisition costs", "Understand expected margins", "Reduce purchasing risk"].map((t) => (
              <li key={t} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-700">
                <span className="h-1.5 w-1.5 rounded-full bg-atlas-blue" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
